import React from 'react';
import _merge from 'lodash/merge';
import MenuIcon from '@nokia-csf-uxr/ccfk-assets/MenuIcon';
import { TRANSPARENT } from '@nokia-csf-uxr/freeform-design-tokens/tokens/colors';
import { getContext } from 'common';
import { zIndex } from 'styles';

import IconButton, { IconButtonProps } from 'components/IconButton';

import Styled from './styles';
import Badge from '../Badge';

const BADGE_STYLE = RTL => ({
  position: 'absolute' as const,
  right: !RTL ? '0.3125rem' : undefined,
  left: RTL ? '0.3125rem' : undefined,
  top: '0.3125rem',
  zIndex: zIndex.BADGE,
});
const BADGE_PROPS = backgroundColor => ({
  border: true,
  borderColor: backgroundColor,
});

export interface SideDrawerButtonProps extends React.ComponentPropsWithRef<'div'> {
  /** If true, the badge will be rendered. */
  badged?: boolean;
  /** The `<IconButton />` props. */
  buttonProps?: Partial<IconButtonProps>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** `SideDrawerButton` is made to be rendered by `AppBanner` component. All other props than `buttonProps` and `badged` will be provided to the root element. */
const SideDrawerButton = React.forwardRef<HTMLElement, SideDrawerButtonProps>((props: SideDrawerButtonProps, forwardedRef) => getContext(({ RTL, theme }) => {
  const {
    badged,
    buttonProps,
    theme: themeProp,
    ...otherProps
  } = props;
  const resolvedTheme = _merge({}, theme ? theme.APP_BANNER_INTERACTIVE_CONTENT : {}, themeProp);
  const iconButtonTheme = {
    content: resolvedTheme.content,
    hovered: {
      container: resolvedTheme.hovered.background,
      content: resolvedTheme.hovered.content
    },
    focused: {
      content: resolvedTheme.focused.content,
      border: resolvedTheme.focused.border,
    },
  };
  const resolvedBackgroundColor = theme && theme.APP_BANNER ? theme.APP_BANNER.background : TRANSPARENT;
  return (
    <Styled.SideDrawerButton $RTL={RTL} ref={forwardedRef} $hoverBorderColor={resolvedTheme.badgeHoveredBorder} {...props} {...otherProps}>
      <IconButton {...buttonProps} theme={iconButtonTheme}>
        {badged && <Badge style={BADGE_STYLE(RTL)} {...BADGE_PROPS(resolvedBackgroundColor)} />}
        <MenuIcon />
      </IconButton>
    </Styled.SideDrawerButton>);
}));

SideDrawerButton.defaultProps = {
  badged: false,
  buttonProps: {},
  theme: {}
};
SideDrawerButton.displayName = 'SideDrawerButton';
export default SideDrawerButton;